/**
 * App — root component.
 *
 * Wires together:
 *  - TanStack Query client (server state)
 *  - Auth context provider (user state)
 *  - TanStack Router (routing + pageview tracking)
 *  - Root error boundary
 *  - React Query devtools (dev only)
 *
 * @spec SPEC:ENGINEERING:FRONTEND:CORE
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from '@/context/AuthContext'
import { CreditsProvider } from '@/context/CreditsContext'
import { OverlayProvider } from '@/context/OverlayContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { router } from '@/router'
import { isDev } from '@/env'
import { events } from '@/services/events'

// Wire pageview events to TanStack Router navigations.
// Fires after each navigation resolves — includes initial load and all SPA transitions.
// Must be set up before <RouterProvider> renders so navigations during startup are captured.
router.subscribe('onResolved', () => {
  events.track('$pageview', {
    $current_url: window.location.href,
    path: window.location.pathname,
  })
})

// Query client configured with sensible defaults.
// Override staleTime per-query in src/queries/ files.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30s default — override per query
      retry: (failureCount, error) => {
        // Don't retry 4xx errors (client errors) — only transient server/network errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status
          if (status >= 400 && status < 500) return false
        }
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
})

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CreditsProvider>
            <OverlayProvider>
              <RouterProvider router={router} />
            </OverlayProvider>
          </CreditsProvider>
        </AuthProvider>
        {isDev && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
